import React, { useState, useRef, useEffect } from "react";

const HOURS = Array.from({ length: 13 }, (_, i) => String(i + 8).padStart(2, "0"));
const MINUTES = ["00", "15", "30", "45"];

function parseValue(value: string | undefined): { hour: string; minute: string } {
  if (!value || !/^\d{1,2}:\d{2}$/.test(value)) return { hour: "", minute: "" };
  const [h, m] = value.split(":").map(Number);
  const hour = Math.min(20, Math.max(8, h));
  const rounded = Math.round(m / 15) * 15;
  const minute = rounded === 60 ? 0 : rounded;
  return {
    hour: String(hour).padStart(2, "0"),
    minute: String(minute).padStart(2, "0"),
  };
}

const inputBaseClass =
  "w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-[#264a3e] focus:border-[#264a3e] bg-white text-left";

interface CustomTimePickerProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  /** Use smaller text to match context (e.g. callback section omits text-sm on inputs). */
  compact?: boolean;
}

export default function CustomTimePicker({
  value,
  onChange,
  placeholder = "Optional",
  className = "",
  compact = false,
}: CustomTimePickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedHour, setSelectedHour] = useState("");
  const [selectedMinute, setSelectedMinute] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);

  const { hour, minute } = parseValue(value);
  const displayText = hour && minute ? `${hour}:${minute}` : "";

  useEffect(() => {
    if (isOpen) {
      setSelectedHour(hour || "");
      setSelectedMinute(minute || "");
    }
  }, [isOpen, hour, minute]);

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  const handleHourClick = (h: string) => {
    setSelectedHour(h);
    if (selectedMinute) {
      onChange(`${h}:${selectedMinute}`);
      setIsOpen(false);
    } else {
      onChange(`${h}:00`);
    }
  };

  const handleMinuteClick = (m: string) => {
    setSelectedMinute(m);
    const hourToUse = selectedHour || HOURS[0];
    onChange(`${hourToUse}:${m}`);
    setIsOpen(false);
  };

  const triggerClass = compact
    ? "w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#264a3e] focus:border-[#264a3e] bg-white text-left"
    : inputBaseClass;

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        className={`${triggerClass} focus:outline-none ${isOpen ? "ring-2 ring-[#264a3e] border-[#264a3e]" : ""}`}
      >
        <span className={displayText ? "text-gray-900" : "text-gray-500"}>{displayText || placeholder}</span>
      </button>

      {isOpen && (
        <div className="absolute left-0 top-full mt-1 z-50 flex bg-white border border-gray-300 rounded-md shadow-lg overflow-hidden w-[88px]">
          <div className="border-r border-gray-200 max-h-[7.5rem] overflow-y-auto py-0.5 [scrollbar-width:thin]">
            {HOURS.map((h) => (
              <button
                key={h}
                type="button"
                onClick={() => handleHourClick(h)}
                className={`block w-full min-w-[2rem] px-2 py-1 text-xs text-center hover:bg-[#264a3e]/10 focus:bg-[#264a3e]/10 focus:outline-none ${
                  selectedHour === h ? "bg-[#264a3e] text-white hover:bg-[#1a3329]" : "text-gray-900"
                }`}
              >
                {h}
              </button>
            ))}
          </div>
          <div className="py-0.5">
            {MINUTES.map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => handleMinuteClick(m)}
                className={`block w-full min-w-[2.25rem] px-2 py-1 text-xs text-center hover:bg-[#264a3e]/10 focus:bg-[#264a3e]/10 focus:outline-none ${
                  selectedMinute === m ? "bg-[#264a3e] text-white hover:bg-[#1a3329]" : "text-gray-900"
                }`}
              >
                {m}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
